import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select, message } from "antd";
import { FileTextOutlined, UploadOutlined } from "@ant-design/icons";
import { API, graphqlOperation } from "aws-amplify";
import Service from "../../services";
import * as mutations from "../../graphql/mutations";
import { errorToString } from "../../utils";
import { GSI_PK, TOUCHSTONE_SUBMISSIONS_STATUS, TOUCHSTONE_SYSTEMS } from "../../utils/touchstone";
import { getSystemConfigValue } from "../../utils/systemConfig";
import { reqSpecToUrls, sourcesToURL } from "../../components/TouchstoneSubmissions/SubmissionSources";

const KNOWN_GIT_REPOS = {
  "github.com:Eon-Labs/ml-feature-set": "git@github.com:Eon-Labs/ml-feature-set.git",
};

const defaultValues = {
  featureSetPath: "ml_feature_set/bundled/",
};

const layout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 18 },
};

export const NewSubmissionButton = ({ userInfo, setShouldFetchData }) => {
  const [config, setConfig] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [reqSpecUrls, setReqSpecUrls] = useState([]);
  const [sourceUrl, setSourceUrl] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    getConfig();
  }, []);

  const getConfig = async () => {
    // get relevant system configs
    const systemConfigs = await Service.listSystemConfigs();
    const reqSpec = getSystemConfigValue(systemConfigs, "touchstone", "requirements_spec");
    setConfig({ defaultRequirementsSpec: reqSpec });
    setReqSpecUrls(reqSpecToUrls(reqSpec));
  };

  const handleValuesChange = (changedValues, allValues) => {
    const changedKeys = Object.keys(changedValues);
    if (["feature_set_git_repo", "feature_set_git_branch", "feature_set_git_path"].some((key) => changedKeys.includes(key))) {
      const feature_set_git_repo = changedValues.feature_set_git_repo || allValues.feature_set_git_repo;
      const feature_set_git_branch = changedValues.feature_set_git_branch || allValues.feature_set_git_branch;
      const feature_set_git_path = changedValues.feature_set_git_path || allValues.feature_set_git_path;
      if (!feature_set_git_repo || !feature_set_git_branch || !feature_set_git_path) {
        setSourceUrl(null);
      } else {
        setSourceUrl(sourcesToURL({ feature_set_git_repo, feature_set_git_branch, feature_set_git_path }));
      }
    }
    if (changedKeys.includes("requirements_spec")) {
      setReqSpecUrls(reqSpecToUrls(changedValues.requirements_spec));
    }
  };

  const makeSubmissionId = (gitPath) => {
    // extact the bare file name of the feature set file from the path
    const pathAfterSlash = gitPath.match(/([^/]+?)(\.py)?\/*$/)[1];
    const randomHex = Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, "0");
    return `${pathAfterSlash}_${randomHex}`;
  };

  const handleSubmit = async () => {
    let formParams, response;
    try {
      formParams = await form.validateFields();
    } catch (error) {
      return;
    }

    const gitPath = formParams.feature_set_git_path;
    const submissionId = makeSubmissionId(gitPath);
    const params = {
      submission_id: submissionId,
      system: TOUCHSTONE_SYSTEMS.FEATURE_SET,
      gsi_pk: GSI_PK,
      owner: userInfo.userId + "::" + userInfo.username,
      description: formParams.description,
      sources: JSON.stringify({
        feature_set_git_repo: formParams.feature_set_git_repo,
        feature_set_git_branch: formParams.feature_set_git_branch,
        feature_set_git_path: gitPath,
        requirements_spec: formParams.requirements_spec,
      }),
      status: TOUCHSTONE_SUBMISSIONS_STATUS.SUBMITTED,
      ts_created: Math.floor(Date.now() / 1000),
    };

    try {
      response = await API.graphql(
        graphqlOperation(mutations.createTouchstoneSubmissions, {
          input: {
            ...params,
          },
        })
      );
    } catch (error) {
      console.log("Form submit failed:", error, "   input:", params);
      message.error(errorToString(error));
      return;
    }
    setIsModalVisible(false);
    setShouldFetchData(true);
    form.resetFields();
    message.success(`Submission ${response.data.createTouchstoneSubmissions.submission_id} created successfully.`);
  };

  if (!config) {
    return null; // let the effect run first
  }

  return (
    <>
      <Button
        type="primary"
        icon={<UploadOutlined />}
        onClick={() => {
          setIsModalVisible(true);
        }}
      >
        New Submission
      </Button>
      <Modal
        title="New Submission"
        width={640}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
        }}
      >
        <Form form={form} onValuesChange={handleValuesChange} {...layout}>
          <Form.Item name="feature_set_git_repo" label="Repository" initialValue={Object.values(KNOWN_GIT_REPOS)[0]}>
            <Select>
              {Object.entries(KNOWN_GIT_REPOS).map(([key, value]) => (
                <Select.Option key={key} value={value}>
                  {key}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="feature_set_git_branch"
            label="Git Branch"
            initialValue="main"
            rules={[{ required: true, message: "Branch name is required" }]}
          >
            <Input placeholder="branch" />
          </Form.Item>
          <Form.Item
            name="feature_set_git_path"
            label="Path in repository"
            initialValue={defaultValues.featureSetPath}
            rules={[{ required: true, message: "Path in repository is required" }]}
          >
            <Input placeholder={defaultValues.featureSetPath + "demo_feature_set.py"} suffix={<ViewSource url={sourceUrl} />} />
          </Form.Item>
          <Form.Item name="requirements_spec" label="Requirements Spec" initialValue={config.defaultRequirementsSpec}>
            <Input
              placeholder={config.defaultRequirementsSpec}
              suffix={reqSpecUrls.map((url, idx) => (
                <span key={idx}>
                  <ViewSource url={url} />
                </span>
              ))}
            />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: false, message: "Please enter a description" }]}>
            <Input.TextArea rows={4} placeholder="Enter description" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const ViewSource = ({ url }) => {
  return (
    <Button type="link" title="View Source" href={url} disabled={!url} target="_blank" style={{ padding: 0, height: 0 }}>
      <FileTextOutlined />
    </Button>
  );
};
